import { FC, useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import tinycolor from 'tinycolor2';

import { checkFormat } from '../../utils';
import { ILabels } from '../Colorpicker/types';
import { getAlphaValue, inputsData, handlePressEnter } from './helpers';
import Input from '../../../../../ui/Input';
import clsx from 'clsx';

interface IChange {
  hex: string;
  alpha: number;
}

type TProps = {
  hex: string;
  alpha: number;
  format?: 'rgb' | 'hsl' | 'hex';
  showAlpha?: boolean;
  onChange: ({ hex, alpha }: IChange) => void;
  onSubmitChange?: (rgba: string) => void;
  labels?: ILabels;
};

const InputRgba: FC<TProps> = ({
  hex,
  alpha,
  format = 'rgb',
  showAlpha = true,
  onChange,
  onSubmitChange,
  labels = {}
}) => {
  const [color, setColor] = useState({
    alpha,
    hex
  });

  const onChangeAlpha = (alpha: string) => {
    const validAlpha = getAlphaValue(alpha);

    setColor({
      ...color,
      alpha: Number(validAlpha)
    });
  };

  const onChangeHex = (hex: string) => {
    setColor({
      ...color,
      hex
    });
  };

  const onHandleSubmit = () => {
    const rgba = tinycolor(color.hex[0] === '#' ? color.hex : '#' + color.hex);
    rgba.setAlpha(Number(color.alpha) / 100);

    if (rgba && (color.alpha !== alpha || color.hex !== hex)) {
      onChange({
        hex: color.hex[0] === '#' ? color.hex : '#' + color.hex,
        alpha: Number(color.alpha)
      });
      if (onSubmitChange) {
        onSubmitChange(
          checkFormat(rgba.toRgbString(), format, showAlpha, color.alpha)
        );
      }
    } else {
      setColor({
        hex,
        alpha
      });
      onChange({
        hex,
        alpha
      });
    }
  };

  useEffect(() => {
    setColor({
      hex,
      alpha
    });
  }, [hex, alpha]);

  const inputsProps = {
    alphaValue: color.alpha,
    hexValue: color.hex.replace(/#/i, ''),
    onChangeAlpha,
    onChangeHex,
    showAlpha,
    labels
  };

  return (
    <div className='mt-3'>
      <div className='flex flex-row items-center gap-2'>
        {inputsData(inputsProps).map((item, index) => {
          const {
            labelSymbol,
            idInput,
            valueInput,
            labelArea,
            onChangeInput,
            name
          } = item;

          const ehAlpha = name === 'alpha';

          return (
            // O hex ocupa a sobra; o alpha tem largura fixa, senão a fileira reflui a cada
            // dígito que entra ou sai.
            <div
              className={clsx(ehAlpha ? 'flex-[0_0_84px]' : 'min-w-0 flex-1')}
              key={index}
            >
              {/* `#` e `%` ficam DENTRO do campo, como no InputNumber da lib: a unidade cola no
                  valor que ela qualifica em vez de flutuar ao lado. São decorativos — quem
                  nomeia o campo para leitor de tela é o aria-label.
                  O foco vem como sombra no wrapper: o Input da lib zera outline e ring com
                  !important, então competir com ele ali seria briga de especificidade. */}
              <div className='relative flex items-center rounded-lg focus-within:shadow-[0_0_0_1px_#9ca3af]'>
                {labelSymbol && (
                  <span className='pointer-events-none absolute left-[9px] select-none text-xs leading-none text-gray-400'>
                    #
                  </span>
                )}

                <Input
                  type='text'
                  id={idInput}
                  value={valueInput}
                  aria-label={labelArea}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeInput(e)}
                  onBlur={onHandleSubmit}
                  onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => handlePressEnter(e, onHandleSubmit)}
                  className={clsx(
                    '!border border-gray-200 tabular-nums',
                    labelSymbol && '!pl-[22px] !pr-2 uppercase tracking-[0.02em]',
                    ehAlpha && '!pl-2 !pr-[22px] text-right'
                  )}
                />

                {ehAlpha && (
                  <span className='pointer-events-none absolute right-[9px] select-none text-xs leading-none text-gray-400'>
                    %
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InputRgba;
